import { useEffect, useState } from 'react';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { PURCHASE_REPORT_PAGE_SIZE, PaginatedReportResponse } from './types';

interface UsePurchaseReportOptions<TParams extends Record<string, unknown>> {
  queryKey: string;
  params: TParams;
  fetcher: (params: TParams & { page: number; page_size: number }) => Promise<PaginatedReportResponse>;
  enabled?: boolean;
  pageSize?: number;
}

export function usePurchaseReport<TParams extends Record<string, unknown>>({
  queryKey,
  params,
  fetcher,
  enabled = true,
  pageSize = PURCHASE_REPORT_PAGE_SIZE,
}: UsePurchaseReportOptions<TParams>) {
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [JSON.stringify(params)]);

  const query = useQuery({
    queryKey: [queryKey, params, page, pageSize],
    queryFn: () => fetcher({ ...params, page, page_size: pageSize }),
    enabled,
  } as UseQueryOptions<PaginatedReportResponse>);

  return {
    ...query,
    page,
    setPage,
    pageSize,
    rows: query.data?.rows ?? [],
    total: query.data?.total ?? 0,
    summary: query.data?.summary ?? {},
  };
}

export async function fetchPurchaseReportExport<TParams extends Record<string, unknown>>(
  fetcher: (params: TParams & { page: number; page_size: number; export: number }) => Promise<PaginatedReportResponse>,
  params: TParams,
) {
  return fetcher({ ...params, page: 0, page_size: PURCHASE_REPORT_PAGE_SIZE, export: 1 });
}
